<template>
  <div class="admin-page">
    <!-- 登录页面 -->
    <div v-if="!isLoggedIn" class="login-container">
      <div class="login-card">
        <div class="login-header">
          <el-icon :size="48"><Lock /></el-icon>
          <h2>安环后台管理</h2>
          <p>安全隐患上报管理系统</p>
        </div>
        <el-form
          ref="loginFormRef"
          :model="loginForm"
          :rules="loginRules"
          label-position="top"
          @submit.prevent="handleLogin"
        >
          <el-form-item label="账号" prop="username">
            <el-input
              v-model="loginForm.username"
              placeholder="请输入管理员账号"
              size="large"
              prefix-icon="User"
            />
          </el-form-item>
          <el-form-item label="密码" prop="password">
            <el-input
              v-model="loginForm.password"
              type="password"
              placeholder="请输入密码"
              size="large"
              prefix-icon="Lock"
              show-password
            />
          </el-form-item>
          <el-form-item>
            <el-button
              type="primary"
              :loading="loginLoading"
              size="large"
              style="width: 100%"
              native-type="submit"
            >
              {{ loginLoading ? '登录中...' : '登 录' }}
            </el-button>
          </el-form-item>
        </el-form>
        <p v-if="loginError" class="login-error">
          <el-icon><Warning /></el-icon> {{ loginError }}
        </p>
      </div>
    </div>

    <!-- 后台管理页面 -->
    <div v-else class="admin-dashboard">
      <!-- 顶部导航 -->
      <header class="admin-header">
        <div class="header-left">
          <el-icon :size="24"><WarningFilled /></el-icon>
          <h2>安全隐患后台管理</h2>
        </div>
        <div class="header-right">
          <el-tag type="success" effect="plain" class="user-tag">
            <el-icon><User /></el-icon> {{ adminUser }}
          </el-tag>
          <el-button text type="danger" @click="handleLogout">
            <el-icon><SwitchButton /></el-icon> 退出
          </el-button>
        </div>
      </header>

      <!-- 筛选区 -->
      <div class="filter-section">
        <div class="quick-date-bar">
          <span class="quick-date-label">快速筛选：</span>
          <span
            v-for="q in quickDates"
            :key="q.key"
            class="quick-date-chip"
            :class="{ on: quickDate === q.key }"
            @click="setQuickDate(q.key)"
          >{{ q.label }}</span>
          <span class="quick-date-tip">默认全部，点选「今日」「近7天」「本月」快速过滤；也可在下方自定义起止日期。</span>
        </div>
        <el-form :inline="true" :model="filters" class="filter-form" label-width="80">
          <el-form-item label="日期起">
            <el-date-picker
              v-model="filters.startDate"
              type="date"
              placeholder="开始日期"
              value-format="YYYY-MM-DD"
              style="width: 140px"
            />
          </el-form-item>
          <el-form-item label="日期止">
            <el-date-picker
              v-model="filters.endDate"
              type="date"
              placeholder="结束日期"
              value-format="YYYY-MM-DD"
              style="width: 140px"
            />
          </el-form-item>
          <el-form-item label="部门">
            <el-select v-model="filters.department" placeholder="全部" clearable style="width: 130px">
              <el-option
                v-for="d in departments"
                :key="d.name"
                :label="d.name"
                :value="d.name"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="科室">
            <el-select
              v-model="filters.subdepartment"
              placeholder="全部"
              clearable
              style="width: 130px"
              :disabled="!filters.department"
            >
              <el-option
                v-for="s in filterSubdepartments"
                :key="s"
                :label="s"
                :value="s"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="类型">
            <el-select v-model="filters.hazardType" placeholder="全部" clearable style="width: 130px">
              <el-option
                v-for="ht in hazardTypeList"
                :key="ht"
                :label="ht"
                :value="ht"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="状态">
            <el-select v-model="filters.status" placeholder="全部" clearable style="width: 130px">
              <el-option label="未处理" value="未处理" />
              <el-option label="处理中" value="处理中" />
              <el-option label="已完成" value="已完成" />
            </el-select>
          </el-form-item>
          <el-form-item label="同步状态">
            <el-select v-model="filters.synced" placeholder="全部" clearable style="width: 130px">
              <el-option label="未同步" value="false" />
              <el-option label="已同步" value="true" />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="loadHazards">
              <el-icon><Search /></el-icon> 查询
            </el-button>
            <el-button @click="resetFilters">
              <el-icon><Refresh /></el-icon> 重置
            </el-button>
          </el-form-item>
        </el-form>
      </div>

      <!-- 操作工具栏 -->
      <div class="toolbar">
        <div class="toolbar-left">
          <span class="record-count">共 {{ totalRecords }} 条记录</span>
        </div>
        <div class="toolbar-right">
          <el-dropdown split-button type="success" @click="exportCSV" trigger="click">
            <el-icon><Download /></el-icon> 导出
            <template #dropdown>
              <el-dropdown-item @click="exportCSV">导出 CSV</el-dropdown-item>
              <el-dropdown-item @click="exportJSON">导出 JSON</el-dropdown-item>
            </template>
          </el-dropdown>
          <el-button type="warning" @click="syncToLocal" :loading="syncLoading">
            <el-icon><Download /></el-icon> 同步到本地
          </el-button>
          <el-button type="danger" @click="downloadSelected" :disabled="selectedRecords.length === 0" :loading="downloadLoading">
            <el-icon><Download /></el-icon> 下载选中({{ selectedRecords.length }})
          </el-button>
          <el-button type="primary" plain @click="openOrgDialog">
            <el-icon><Setting /></el-icon> 组织架构
          </el-button>
          <el-button text @click="refreshData">
            <el-icon><Refresh /></el-icon> 刷新
          </el-button>
        </div>
      </div>

      <!-- 数据表格 -->
      <div class="table-container">
        <el-table
          :data="hazards"
          v-loading="tableLoading"
          stripe
          style="width: 100%"
          max-height="600"
          @sort-change="handleSortChange"
          @selection-change="onSelectionChange"
        >
          <el-table-column type="selection" width="45" />
          <el-table-column prop="id" label="隐患编号" width="140" sortable="custom" />
          <el-table-column prop="reportTime" label="上报时间" width="170" sortable="custom">
            <template #default="{ row }">
              {{ formatTime(row.reportTime) }}
            </template>
          </el-table-column>
          <el-table-column prop="department" label="部门" width="100" />
          <el-table-column prop="subdepartment" label="科室" width="100" />
          <el-table-column prop="area" label="区域" width="120" />
          <el-table-column prop="hazardType" label="类型" width="100">
            <template #default="{ row }">
              <el-tag :type="typeTagType(row.hazardType)" size="small">{{ row.hazardType }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="description" label="描述" min-width="180" show-overflow-tooltip />
          <el-table-column prop="reporter" label="上报人" width="80" />
          <el-table-column label="图片" width="100" align="center">
            <template #default="{ row }">
              <el-button text type="primary" size="small" @click="previewImage(row)">
                <el-icon><View /></el-icon> 预览
              </el-button>
            </template>
          </el-table-column>
          <el-table-column prop="status" label="状态" width="110">
            <template #default="{ row }">
              <el-select
                v-model="row.status"
                size="small"
                :teleported="false"
                @change="(val) => updateStatus(row, val)"
                :class="'status-' + row.status"
              >
                <el-option label="未处理" value="未处理">
                  <el-tag type="danger" size="small">未处理</el-tag>
                </el-option>
                <el-option label="处理中" value="处理中">
                  <el-tag type="warning" size="small">处理中</el-tag>
                </el-option>
                <el-option label="已完成" value="已完成">
                  <el-tag type="success" size="small">已完成</el-tag>
                </el-option>
              </el-select>
            </template>
          </el-table-column>
          <el-table-column prop="synced" label="同步" width="80" align="center">
            <template #default="{ row }">
              <el-tag v-if="row.synced" type="success" size="small">已同步</el-tag>
              <el-tag v-else type="danger" size="small">未同步</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="80" align="center" fixed="right">
            <template #default="{ row }">
              <div class="action-btns">
                <el-tooltip content="下载图片" placement="top">
                  <el-button text type="primary" size="small" @click="downloadImage(row)">
                    <el-icon><Download /></el-icon>
                  </el-button>
                </el-tooltip>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- 分页 -->
      <div class="pagination-wrapper">
        <el-pagination
          v-model:current-page="pagination.current"
          v-model:page-size="pagination.pageSize"
          :total="totalRecords"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next"
          background
          @size-change="loadHazards"
          @current-change="loadHazards"
        />
      </div>
    </div>

    <!-- 图片预览对话框 -->
    <el-dialog
      v-model="previewDialogVisible"
      title="隐患媒体预览"
      width="90%"
      :close-on-click-modal="true"
      class="preview-dialog"
    >
      <div v-if="previewMediaList.length" class="preview-body">
        <div class="preview-stage">
          <img v-if="currentMedia.type === 'image'" :src="currentMedia.url" alt="隐患照片" class="preview-img" />
          <video v-else :src="currentMedia.url" controls class="preview-video"></video>
          <button v-if="previewMediaList.length > 1" class="preview-nav prev" @click="prevMedia">‹</button>
          <button v-if="previewMediaList.length > 1" class="preview-nav next" @click="nextMedia">›</button>
        </div>
        <div v-if="previewMediaList.length > 1" class="preview-thumbs">
          <div
            v-for="(m, i) in previewMediaList"
            :key="i"
            class="thumb"
            :class="{ active: i === previewMediaIndex }"
            @click="previewMediaIndex = i"
          >
            <img v-if="m.type === 'image'" :src="m.url" />
            <div v-else class="thumb-video">
              <el-icon :size="20"><VideoPlay /></el-icon>
            </div>
            <span class="thumb-label">{{ i + 1 }}</span>
          </div>
        </div>
      </div>
      <template #footer>
        <span class="preview-count">{{ previewMediaIndex + 1 }} / {{ previewMediaList.length }}</span>
        <el-button v-if="currentMedia.type === 'image'" type="primary" @click="downloadCurrentImage">
          <el-icon><Download /></el-icon> 下载图片
        </el-button>
      </template>
    </el-dialog>

    <!-- 组织架构管理对话框 -->
    <el-dialog
      v-model="orgDialogVisible"
      title="组织架构管理（部门 / 科室）"
      width="92%"
      :close-on-click-modal="false"
      class="org-dialog"
    >
      <div class="org-toolbar">
        <div>
          <el-button size="small" type="primary" @click="addOrgRow">
            <el-icon><Plus /></el-icon> 添加部门
          </el-button>
          <el-button size="small" @click="pasteMode = !pasteMode">
            <el-icon><Document /></el-icon> {{ pasteMode ? '返回编辑' : '批量粘贴导入' }}
          </el-button>
        </div>
        <el-button size="small" type="success" :loading="orgSaving" @click="saveOrg">
          <el-icon><Upload /></el-icon> 保存并发布
        </el-button>
      </div>

      <!-- 批量粘贴导入模式 -->
      <div v-if="pasteMode" class="org-paste">
        <p class="org-tip">每行一条：<b>部门,科室</b>（逗号分隔）。同一部门多行会自动合并科室。</p>
        <el-input type="textarea" :rows="8" v-model="pasteText" placeholder="安全环境室,安全环境科&#10;安全环境室,源动科&#10;部门1,科室1" />
        <div style="margin-top:8px;text-align:right">
          <el-button size="small" type="primary" @click="applyPaste">解析并填入</el-button>
        </div>
      </div>

      <!-- 编辑表格 -->
      <el-table v-else :data="orgRows" size="small" border>
        <el-table-column type="index" width="45" />
        <el-table-column label="部门">
          <template #default="{ row }">
            <el-input v-model="row.name" size="small" placeholder="部门名称" />
          </template>
        </el-table-column>
        <el-table-column label="科室（逗号分隔）">
          <template #default="{ row }">
            <el-input v-model="row.subs" size="small" placeholder="科室1,科室2" />
          </template>
        </el-table-column>
        <el-table-column label="操作" width="70" align="center">
          <template #default="{ $index }">
            <el-button text type="danger" size="small" @click="removeOrgRow($index)">
              <el-icon><Delete /></el-icon>
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="org-foot-tip">
        说明：保存即发布到云端，上报页与后台「部门/科室」即时生效，无需重新部署。
        本机 Excel 文件批量导入请使用 <code>org-tool/run.bat</code>。
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { fetchHazards, loginAdmin, updateHazard, getImageUrl, getVideoUrl, getDownloadUrl, exportHazards, fetchOrg, updateOrg, markSynced } from '../api'
import { store as auth, login as authLogin, logout as authLogout, handleAuthExpired } from '../api/authState.js'
import dayjs from 'dayjs'

// ====== 登录状态 ======
// 登录态来自共享的 authState 模块（token 与 localStorage 双向同步）：
// 任意请求遇到 401 时模块会清空 token，这里立即变为未登录并渲染登录卡片，无需手动刷新浏览器
const isLoggedIn = computed(() => !!auth.token)
const adminUser = computed(() => auth.user || '管理员')
const loginLoading = ref(false)
const loginError = ref('')
const loginFormRef = ref(null)

const loginForm = reactive({
  username: '',
  password: ''
})

const loginRules = {
  username: [{ required: true, message: '请输入账号', trigger: 'blur' }],
  password: [{ required: true, message: '请输入密码', trigger: 'blur' }]
}

async function handleLogin() {
  if (!loginFormRef.value) return
  const valid = await loginFormRef.value.validate().catch(() => false)
  if (!valid) return

  loginLoading.value = true
  loginError.value = ''
  try {
    const result = await loginAdmin({
      username: loginForm.username,
      password: loginForm.password
    })
    // 同时写入共享 ref 与 localStorage，isLoggedIn 自动变为 true
    authLogin(result.token, loginForm.username)
    ElMessage.success('登录成功')
    loadHazards()
  } catch (err) {
    // 登录失败的 401（如账号密码错误）由 loginAdmin 的 skipAuthExpiry 放行，
    // 这里显示的是服务端返回的真实原因
    loginError.value = err.message || '登录失败，请检查账号密码'
  } finally {
    loginLoading.value = false
  }
}

function handleLogout() {
  ElMessageBox.confirm('确认退出登录？', '提示').then(() => {
    authLogout()
    ElMessage.success('已退出')
  }).catch(() => {})
}

// ====== 数据加载 ======
const hazards = ref([])
const totalRecords = ref(0)
const tableLoading = ref(false)

const filters = reactive({
  startDate: '',
  endDate: '',
  department: '',
  subdepartment: '',
  hazardType: '',
  status: '',
  synced: ''
})

// ====== 快速日期筛选（今日 / 近7天 / 本月 / 全部） ======
const quickDates = [
  { key: 'today', label: '今日' },
  { key: 'week', label: '近7天' },
  { key: 'month', label: '本月' },
  { key: 'all', label: '全部' }
]
const quickDate = ref('all')
let skipQuickWatch = false

function dstr(d) {
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
}

function setQuickDate(key) {
  skipQuickWatch = true
  quickDate.value = key
  const today = new Date()
  if (key === 'today') {
    filters.startDate = dstr(today)
    filters.endDate = dstr(today)
  } else if (key === 'week') {
    const t7 = new Date(today)
    t7.setDate(t7.getDate() - 6)
    filters.startDate = dstr(t7)
    filters.endDate = dstr(today)
  } else if (key === 'month') {
    filters.startDate = today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0') + '-01'
    filters.endDate = dstr(today)
  } else {
    filters.startDate = ''
    filters.endDate = ''
  }
  skipQuickWatch = false
  pagination.current = 1
  loadHazards()
}

// 手动修改起止日期视为「自定义」，取消快捷高亮
watch([() => filters.startDate, () => filters.endDate], () => {
  if (skipQuickWatch) return
  quickDate.value = 'custom'
})

const pagination = reactive({
  current: 1,
  pageSize: 20
})

const departments = ref([])

const filterSubdepartments = computed(() => {
  const dept = departments.value.find(d => d.name === filters.department)
  return dept ? (dept.subdepartments || []) : []
})

watch(() => filters.department, () => {
  filters.subdepartment = ''
})

async function loadOrg() {
  try {
    const r = await fetchOrg()
    departments.value = r.departments || []
  } catch (e) {
    console.warn('组织架构加载失败：', e.message)
  }
}

const hazardTypeList = [
  '设备隐患', '电气隐患', '消防隐患', '化学品隐患',
  '作业行为隐患', '5S隐患', '通道占用', '其他'
]

function formatTime(t) {
  return t ? dayjs(t).format('YYYY-MM-DD HH:mm') : '-'
}

function typeTagType(type) {
  const map = {
    '设备隐患': 'warning',
    '电气隐患': 'danger',
    '消防隐患': 'danger',
    '化学品隐患': 'danger',
    '作业行为隐患': 'info',
    '5S隐患': '',
    '通道占用': 'warning',
    '其他': 'info'
  }
  return map[type] || ''
}

async function loadHazards() {
  tableLoading.value = true
  try {
    const params = {
      page: pagination.current,
      pageSize: pagination.pageSize,
      startDate: filters.startDate || undefined,
      endDate: filters.endDate || undefined,
      department: filters.department || undefined,
      subdepartment: filters.subdepartment || undefined,
      hazardType: filters.hazardType || undefined,
      status: filters.status || undefined,
      synced: filters.synced !== '' ? filters.synced : undefined,
      sortField: sortField.value,
      sortOrder: sortOrder.value
    }
    const result = await fetchHazards(params)
    hazards.value = result.data || []
    totalRecords.value = result.total || 0
  } catch (err) {
    // 401：登录态已被清空，登录卡片会自动渲染；重新登录的提示由 authState 统一弹出，这里不重复弹
    if (!err.isAuthExpired) {
      ElMessage.error('加载数据失败：' + err.message)
    }
  } finally {
    tableLoading.value = false
  }
}

function refreshData() {
  loadHazards()
  ElMessage.success('已刷新')
}

function resetFilters() {
  skipQuickWatch = true
  filters.startDate = ''
  filters.endDate = ''
  filters.department = ''
  filters.subdepartment = ''
  filters.hazardType = ''
  filters.status = ''
  filters.synced = ''
  quickDate.value = 'all'
  skipQuickWatch = false
  pagination.current = 1
  loadHazards()
}

// ====== 排序 ======
const sortField = ref('reportTime')
const sortOrder = ref('desc')

function handleSortChange({ prop, order }) {
  if (prop) {
    sortField.value = prop
    sortOrder.value = order === 'ascending' ? 'asc' : 'desc'
  }
  loadHazards()
}

// ====== 状态更新 ======
async function updateStatus(row, newStatus) {
  try {
    await updateHazard({ id: row.id, status: newStatus })
    ElMessage.success('状态已更新')
  } catch (err) {
    ElMessage.error('更新失败：' + err.message)
    loadHazards() // 重新加载恢复原始状态
  }
}

// ====== 组织架构管理 ======
const orgDialogVisible = ref(false)
const orgSaving = ref(false)
const orgRows = ref([])
const pasteMode = ref(false)
const pasteText = ref('')

function openOrgDialog() {
  orgRows.value = departments.value.map(d => ({
    name: d.name,
    subs: (d.subdepartments || []).join(',')
  }))
  pasteMode.value = false
  pasteText.value = ''
  orgDialogVisible.value = true
}

function addOrgRow() {
  orgRows.value.push({ name: '', subs: '' })
}

function removeOrgRow(i) {
  orgRows.value.splice(i, 1)
}

function applyPaste() {
  const map = new Map() // dept -> [subs]
  for (const line of pasteText.value.split(/\r?\n/)) {
    const parts = line.split(/[,，、]/).map(s => s.trim()).filter(Boolean)
    if (!parts.length) continue
    const dept = parts[0]
    const sub = parts[1] || ''
    if (!map.has(dept)) map.set(dept, [])
    if (sub && !map.get(dept).includes(sub)) map.get(dept).push(sub)
  }
  orgRows.value = Array.from(map.entries()).map(([name, subs]) => ({ name, subs: subs.join(',') }))
  pasteMode.value = false
  if (!orgRows.value.length) ElMessage.warning('未解析到任何数据')
  else ElMessage.success(`已解析 ${orgRows.value.length} 个部门`)
}

async function saveOrg() {
  const departmentsData = orgRows.value
    .map(r => ({
      name: (r.name || '').trim(),
      subdepartments: (r.subs || '').split(/[,，、]/).map(s => s.trim()).filter(Boolean)
    }))
    .filter(d => d.name)
  if (!departmentsData.length) { ElMessage.warning('请至少填写一个部门'); return }
  orgSaving.value = true
  try {
    const result = await updateOrg(departmentsData)
    await loadOrg()
    ElMessage.success(`已发布 ${result.count || departmentsData.length} 个部门，线上已生效`)
    orgDialogVisible.value = false
  } catch (e) {
    ElMessage.error('发布失败：' + e.message)
  } finally {
    orgSaving.value = false
  }
}

// ====== 图片/视频预览和下载 ======
const previewDialogVisible = ref(false)
const previewMediaList = ref([])   // [{ type: 'image'|'video', url, index }]
const previewMediaIndex = ref(0)
const previewImageId = ref('')

const currentMedia = computed(() => previewMediaList.value[previewMediaIndex.value] || {})

function previewImage(row) {
  previewImageId.value = row.id
  const list = []
  // 图片：优先 images 数组，兼容旧 imagePath
  if (Array.isArray(row.images) && row.images.length > 0) {
    row.images.forEach((_, i) => list.push({ type: 'image', url: getImageUrl(row.id, i), index: i }))
  } else if (row.imagePath) {
    list.push({ type: 'image', url: getImageUrl(row.id, 0), index: 0 })
  }
  // 视频
  if (Array.isArray(row.videos) && row.videos.length > 0) {
    row.videos.forEach((_, i) => list.push({ type: 'video', url: getVideoUrl(row.id, i), index: i }))
  }
  previewMediaList.value = list
  previewMediaIndex.value = 0
  previewDialogVisible.value = true
}

function prevMedia() {
  previewMediaIndex.value = (previewMediaIndex.value - 1 + previewMediaList.value.length) % previewMediaList.value.length
}
function nextMedia() {
  previewMediaIndex.value = (previewMediaIndex.value + 1) % previewMediaList.value.length
}

function downloadImage(row) {
  const idx = (Array.isArray(row.images) && row.images.length > 0) ? 0 : 0
  const url = getDownloadUrl(row.id, idx)
  const a = document.createElement('a')
  a.href = url
  a.download = `${row.id}.jpg`
  a.target = '_blank'
  a.click()
  ElMessage.success('开始下载')
}

function downloadCurrentImage() {
  const cur = currentMedia.value
  if (cur.type !== 'image') return
  const url = getDownloadUrl(previewImageId.value, cur.index)
  const a = document.createElement('a')
  a.href = url
  a.download = `${previewImageId.value}_${cur.index + 1}.jpg`
  a.target = '_blank'
  a.click()
}

// ====== 导出 ======
async function exportCSV() {
  try {
    const params = {
      startDate: filters.startDate || undefined,
      endDate: filters.endDate || undefined,
      department: filters.department || undefined,
      subdepartment: filters.subdepartment || undefined,
      hazardType: filters.hazardType || undefined,
      status: filters.status || undefined,
      format: 'csv'
    }
    const response = await exportHazards(params)
    const blob = await response.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `隐患记录_${dayjs().format('YYYYMMDDHHmmss')}.csv`
    a.click()
    URL.revokeObjectURL(url)
    ElMessage.success('导出成功')
  } catch (err) {
    ElMessage.error('导出失败：' + err.message)
  }
}

async function exportJSON() {
  try {
    const params = {
      startDate: filters.startDate || undefined,
      endDate: filters.endDate || undefined,
      department: filters.department || undefined,
      subdepartment: filters.subdepartment || undefined,
      hazardType: filters.hazardType || undefined,
      status: filters.status || undefined,
      format: 'json'
    }
    const response = await exportHazards(params)
    const blob = await response.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `隐患记录_${dayjs().format('YYYYMMDDHHmmss')}.json`
    a.click()
    URL.revokeObjectURL(url)
    ElMessage.success('导出成功')
  } catch (err) {
    ElMessage.error('导出失败：' + err.message)
  }
}

// ====== 同步到本地 ======
const syncLoading = ref(false)
const LOCAL_SYNC_URL = 'http://localhost:18888'

async function syncToLocal() {
  syncLoading.value = true
  try {
    // 1. 检查本地同步工具是否运行
    const pingResp = await fetch(`${LOCAL_SYNC_URL}/api/_records?pageSize=1`).catch(() => null)
    if (!pingResp) {
      ElMessage.warning('请先运行本地同步程序 (run.bat)')
      syncLoading.value = false
      return
    }

    // 2. 从云端获取未同步的记录
    ElMessage.info('正在从云端获取数据...')
    let allRecords = []
    let page = 1
    while (true) {
      // 走统一的 request()：401 会清空登录态并提示重新登录（API 与 raw fetch 行为一致）
      let result
      try {
        result = await fetchHazards({ synced: 'false', pageSize: 50, page })
      } catch (err) {
        // 401 时登录态已被清空，「登录已过期，请重新登录」由 authState 统一弹出，这里只补充场景说明
        ElMessage.error(err.isAuthExpired ? '获取数据失败：登录已过期，请重新登录' : '同步失败：' + err.message)
        syncLoading.value = false
        return
      }
      allRecords = allRecords.concat(result.data || [])
      if (allRecords.length >= (result.total || 0)) break
      page++
    }

    if (allRecords.length === 0) {
      ElMessage.success('没有新的未同步记录')
      syncLoading.value = false
      return
    }

    // 3. 存储记录到本地
    ElMessage.info(`正在同步 ${allRecords.length} 条记录...`)
    await fetch(`${LOCAL_SYNC_URL}/api/_store`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(allRecords)
    })

    // 4. 下载图片
    let imgCount = 0
    let authFailed = false
    for (const rec of allRecords) {
      try {
        // 保持 ?token= 方式不变；token 从共享登录态读取
        const imgResp = await fetch(`/api/download?id=${rec.id}&token=${encodeURIComponent(auth.token)}`)
        if (imgResp.status === 401) {
          // 401：清空登录态（界面立即回到登录页），中止本次同步，不再继续下载/标记
          handleAuthExpired()
          authFailed = true
          break
        }
        if (!imgResp.ok) continue
        const blob = await imgResp.blob()
        // Convert to base64
        const b64 = await new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => resolve(reader.result.split(',')[1])
          reader.onerror = reject
          reader.readAsDataURL(blob)
        })
        await fetch(`${LOCAL_SYNC_URL}/api/_store-image`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: rec.id, data: b64, contentType: blob.type })
        })
        imgCount++
      } catch (e) {
        console.error(`Image download failed for ${rec.id}:`, e)
      }
    }

    // 登录态已失效：不再继续标记同步（提示由 authState 统一弹出，这里只说明同步被中止）
    if (authFailed || !auth.token) {
      ElMessage.warning('登录已过期，本次同步已中止，请重新登录后再同步')
      return
    }

    // 5. 标记云端已同步
    const ids = allRecords.map(r => r.id)
    await markSynced(ids)

    // 6. 标记本地已同步
    await fetch(`${LOCAL_SYNC_URL}/api/_mark-synced`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids })
    })

    ElMessage.success(`同步完成：${allRecords.length} 条记录，${imgCount} 张图片`)
    loadHazards()
  } catch (err) {
    ElMessage.error('同步失败：' + err.message)
  } finally {
    syncLoading.value = false
  }
}

// ====== 下载选中（导出 JSON + base64 图片到本地文件） ======
const selectedRecords = ref([])
const downloadLoading = ref(false)

function onSelectionChange(selection) {
  selectedRecords.value = selection
}

async function downloadSelected() {
  const records = selectedRecords.value
  if (records.length === 0) {
    ElMessage.warning('请先勾选要导出的记录')
    return
  }
  downloadLoading.value = true
  ElMessage.info(`正在导出 ${records.length} 条记录，下载图片中...`)
  try {
    const exportData = []
    let imgOk = 0, imgFail = 0
    let authFailed = false
    for (const rec of records) {
      const item = { ...rec, imageData: null, imageContentType: null }
      // 只有记录有 imagePath 才尝试下载图片
      if (rec.imagePath) {
        try {
          // 保持 ?token= 方式不变；token 从共享登录态读取
          const imgResp = await fetch(`/api/image?id=${rec.id}&token=${encodeURIComponent(auth.token)}`)
          if (imgResp.status === 401) {
            // 401：清空登录态（界面立即回到登录页），停止导出，不再输出不完整的文件
            handleAuthExpired()
            authFailed = true
            break
          } else if (imgResp.ok) {
            const blob = await imgResp.blob()
            const base64 = await new Promise((resolve, reject) => {
              const reader = new FileReader()
              reader.onload = () => resolve(reader.result.split(',')[1])
              reader.onerror = reject
              reader.readAsDataURL(blob)
            })
            item.imageData = base64
            item.imageContentType = blob.type
            imgOk++
          } else {
            const errText = await imgResp.text().catch(() => '')
            console.warn(`图片下载失败 ${rec.id}: HTTP ${imgResp.status} ${errText}`)
            imgFail++
          }
        } catch (e) {
          console.warn(`图片下载异常 ${rec.id}:`, e.message || e)
          imgFail++
        }
      }
      exportData.push(item)
    }
    if (authFailed || !auth.token) {
      ElMessage.warning('登录已过期，本次导出已中止，请重新登录后再导出')
      return
    }
    const blob = new Blob([JSON.stringify({
      exportTime: new Date().toISOString(),
      total: exportData.length,
      records: exportData
    }, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `隐患数据_${dayjs().format('YYYYMMDDHHmmss')}.json`
    a.click()
    URL.revokeObjectURL(url)
    const imgMsg = imgOk > 0 ? `，${imgOk} 张图片` : ''
    const failMsg = imgFail > 0 ? `（${imgFail} 张下载失败，详见控制台）` : ''
    ElMessage.success(`导出成功：${exportData.length} 条记录${imgMsg}${failMsg}`)
  } catch (err) {
    ElMessage.error('导出失败：' + err.message)
  } finally {
    downloadLoading.value = false
  }
}

// ====== 初始化 ======
onMounted(() => {
  loadOrg()
  // 登录态由 authState 模块在加载时自行从 localStorage 播种，这里只需决定是否拉取数据
  if (isLoggedIn.value) {
    loadHazards()
  }
})
</script>

<style scoped>
.admin-page {
  min-height: 100vh;
  background: #f0f2f5;
}

/* 登录 */
.login-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
}

.login-card {
  background: #fff;
  border-radius: 16px;
  padding: 40px 32px;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.15);
}

.login-header {
  text-align: center;
  margin-bottom: 32px;
}

.login-header h2 {
  margin: 12px 0 6px;
  font-size: 22px;
  color: #303133;
}

.login-header p {
  font-size: 14px;
  color: #909399;
}

.login-error {
  color: #f56c6c;
  font-size: 13px;
  text-align: center;
  margin-top: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

/* 后台布局 */
.admin-header {
  background: #fff;
  padding: 12px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 1px 4px rgba(0,0,0,0.08);
  position: sticky;
  top: 0;
  z-index: 100;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.header-left h2 {
  font-size: 18px;
  font-weight: 600;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-tag {
  display: flex;
  align-items: center;
  gap: 4px;
}

/* 筛选区 */
.filter-section {
  background: #fff;
  margin: 16px;
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
}

.filter-form {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

/* 快速日期筛选 */
.quick-date-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}
.quick-date-label {
  font-size: 13px;
  color: #606266;
  font-weight: 600;
}
.quick-date-chip {
  padding: 5px 16px;
  border: 1px solid #dce3f0;
  border-radius: 16px;
  background: #fff;
  color: #5a6488;
  font-size: 13px;
  cursor: pointer;
  transition: all .15s;
  user-select: none;
}
.quick-date-chip:hover {
  border-color: #4f6ef7;
  color: #4f6ef7;
}
.quick-date-chip.on {
  background: #4f6ef7;
  color: #fff;
  border-color: #4f6ef7;
}
.quick-date-tip {
  font-size: 12px;
  color: #a8abb2;
}

/* 工具栏 */
.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 16px 12px;
}

.record-count {
  color: #909399;
  font-size: 14px;
}

.toolbar-right {
  display: flex;
  gap: 8px;
}

/* 表格 */
.table-container {
  margin: 0 16px;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
}

.action-btns {
  display: flex;
  gap: 4px;
  justify-content: center;
}

/* 分页 */
.pagination-wrapper {
  padding: 16px;
  display: flex;
  justify-content: flex-end;
}

/* 状态选择器 */
:deep(.status-未处理 .el-select__wrapper) {
  border-color: #f56c6c;
}

:deep(.status-处理中 .el-select__wrapper) {
  border-color: #e6a23c;
}

:deep(.status-已完成 .el-select__wrapper) {
  border-color: #67c23a;
}

/* 预览 */
.preview-body {
  text-align: center;
}

.preview-img {
  max-width: 100%;
  max-height: 70vh;
  border-radius: 8px;
}

.preview-dialog :deep(.el-dialog__body) {
  padding: 16px;
}

.preview-stage {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
}

.preview-video {
  max-width: 100%;
  max-height: 70vh;
  border-radius: 8px;
  background: #000;
}

.preview-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.5);
  color: #fff;
  font-size: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
}
.preview-nav:hover { background: rgba(0, 0, 0, 0.75); }
.preview-nav.prev { left: 8px; }
.preview-nav.next { right: 8px; }

.preview-thumbs {
  display: flex;
  gap: 8px;
  margin-top: 12px;
  justify-content: center;
  flex-wrap: wrap;
}

.thumb {
  position: relative;
  width: 60px;
  height: 60px;
  border-radius: 6px;
  overflow: hidden;
  border: 2px solid transparent;
  cursor: pointer;
  background: #f5f7fa;
}
.thumb.active { border-color: #409eff; }
.thumb img { width: 100%; height: 100%; object-fit: cover; }
.thumb-video {
  width: 100%; height: 100%;
  display: flex; align-items: center; justify-content: center;
  background: #303133; color: #fff;
}
.thumb-label {
  position: absolute;
  bottom: 2px; right: 4px;
  font-size: 10px;
  color: #fff;
  text-shadow: 0 1px 2px rgba(0,0,0,0.8);
}

.preview-count {
  float: left;
  line-height: 32px;
  color: #909399;
  font-size: 13px;
}

/* 组织架构管理 */
.org-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  flex-wrap: wrap;
  gap: 8px;
}

.org-paste {
  margin-bottom: 12px;
}

.org-tip {
  font-size: 12.5px;
  color: #909399;
  margin-bottom: 8px;
}

.org-foot-tip {
  margin-top: 12px;
  font-size: 12px;
  color: #909399;
}

.org-foot-tip code {
  background: #f5f7fa;
  padding: 1px 4px;
  border-radius: 4px;
  color: #606266;
}

/* 响应式 */
@media (max-width: 768px) {
  .admin-header {
    padding: 10px 16px;
    flex-direction: column;
    gap: 8px;
  }
  .filter-section {
    margin: 8px;
    padding: 12px;
  }
  .filter-form {
    flex-direction: column;
  }
  .filter-form .el-form-item {
    margin-bottom: 8px;
  }
  .toolbar {
    flex-direction: column;
    gap: 8px;
    align-items: stretch;
  }
  .table-container {
    margin: 0 8px;
    overflow-x: auto;
  }
  .pagination-wrapper {
    justify-content: center;
  }
}
</style>